import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Button } from '../../ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { Upload, X, CreditCard } from 'lucide-react'
import { ColorPicker } from '../../ui/color-picker'
import EventEndpoints from '../../../endpoints/EventEndpoints'
import BadgeTemplateEndpoints, { BadgeTemplate } from '../../../endpoints/BadgeTemplateEndpoints'
import { BadgePropertyOverrides } from '../../common/BadgePropertyOverrides'
import { useEventContext } from '../EventContext'
import { toast } from 'sonner'
import * as util from '../../../common/Util'
import { BadgePrintingLogic } from '../../../common/BadgePrintingLogic'
import { BadgeConfigurationRequestFromBadgeConfiguration, DefaultBadgeConfigurationRequest } from '../../../requests/BadgeConfigurationRequest'

interface BadgeConfiguration {
  badgeName: string
  templateId: number
  primaryColour: string
  secondaryColour: string
  roleTextColour: string
  propertyOverrides: Record<string, string>
  logoImageContentType?: string | null
  base64LogoImage?: string | null
  backImageContentType?: string | null
  base64BackImage?: string | null
}

const defaultBadgeConfig: BadgeConfiguration = {
  badgeName: '',
  templateId: 1,
  primaryColour: '#1e40af',
  secondaryColour: '#3b82f6',
  roleTextColour: '#000000',
  propertyOverrides: {},
  logoImageContentType: null,
  base64LogoImage: null,
  backImageContentType: null,
  base64BackImage: null,
}

export const BadgeConfiguration: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const { event, setEvent, canEdit } = useEventContext()
  const [saving, setSaving] = useState(false)
  const [templates, setTemplates] = useState<BadgeTemplate[]>([])
  const [config, setConfig] = useState<BadgeConfiguration>({
    ...defaultBadgeConfig,
  })
  const [configLoaded, setConfigLoaded] = useState(false)

  // Fallback templates for when the API isn't available yet
  const fallbackTemplates: BadgeTemplate[] = [
    { id: 1, name: 'Vertical (Default)', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'PORTRAIT', size: 'STANDARD', width: 210, height: 325, createdBy: 'System', createdAt: '', updatedAt: '' },
    { id: 2, name: 'Horizontal', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'LANDSCAPE', size: 'STANDARD', width: 325, height: 210, createdBy: 'System', createdAt: '', updatedAt: '' },
  ]

  const clientId = event?.client?.id

  useEffect(() => {
    if (clientId == null) return
    BadgeTemplateEndpoints.getTemplates(true, undefined, clientId)
      .then((data) => setTemplates(data.length > 0 ? data : fallbackTemplates))
      .catch(() => setTemplates(fallbackTemplates))
  }, [clientId])

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === config.templateId) || null
  }, [templates, config.templateId])

  useEffect(() => {
    if (!event || configLoaded) return
    if (event.badgeConfiguration) {
      const badgeRequest = BadgeConfigurationRequestFromBadgeConfiguration(event.badgeConfiguration)
      setConfig({
        badgeName: badgeRequest.badgeName || '',
        templateId: badgeRequest.templateId || 1,
        primaryColour: badgeRequest.primaryColour || '#1c5ca2',
        secondaryColour: badgeRequest.secondaryColour || '#304c75',
        roleTextColour: badgeRequest.roleTextColour || '#ffffff',
        propertyOverrides: event.badgeConfiguration.propertyOverrides || {},
        logoImageContentType: badgeRequest.logoImageContentType,
        base64LogoImage: badgeRequest.base64LogoImage,
        backImageContentType: badgeRequest.backImageContentType,
        base64BackImage: badgeRequest.base64BackImage,
      })
    } else {
      const defaultRequest = DefaultBadgeConfigurationRequest()
      setConfig({
        ...defaultRequest,
        propertyOverrides: {},
        badgeName: event.name || 'Event Badge'
      })
    }
    setConfigLoaded(true)
  }, [event])

  const handleSave = async () => {
    if (!eventId) return

    try {
      setSaving(true)

      // Save each badge configuration field using the editEvent endpoint
      const overridesJson = Object.keys(config.propertyOverrides).length > 0
        ? JSON.stringify(config.propertyOverrides)
        : 'null'
      const updates = [
        { field: 'badge.badgeName', value: config.badgeName },
        { field: 'badge.templateId', value: config.templateId },
        { field: 'badge.primaryColour', value: config.primaryColour },
        { field: 'badge.secondaryColour', value: config.secondaryColour },
        { field: 'badge.roleTextColour', value: config.roleTextColour },
        { field: 'badge.propertyOverrides', value: overridesJson },
      ]

      // Handle logo image
      if (config.base64LogoImage && config.logoImageContentType) {
        updates.push({
          field: 'badge.logoImage',
          value: JSON.stringify({
            base64Image: config.base64LogoImage,
            contentType: config.logoImageContentType
          })
        })
      } else {
        updates.push({ field: 'badge.logoImage', value: 'null' })
      }

      // Handle back image
      if (config.base64BackImage && config.backImageContentType) {
        updates.push({
          field: 'badge.backImage',
          value: JSON.stringify({
            base64Image: config.base64BackImage,
            contentType: config.backImageContentType
          })
        })
      } else {
        updates.push({ field: 'badge.backImage', value: 'null' })
      }

      // Apply all updates
      for (const update of updates) {
        await EventEndpoints.editEvent(parseInt(eventId), update)
      }

      // Reload the event to get updated data
      const updatedEvent = await EventEndpoints.getEvent(parseInt(eventId))
      setEvent(updatedEvent)

      toast.success('Badge configuration saved successfully')
    } catch (error: any) {
      console.error('Failed to save badge configuration:', error)
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save configuration'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = useCallback((fieldName: keyof BadgeConfiguration) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const newConfig = { ...config }
      newConfig[fieldName] = e.target.value
      setConfig(newConfig)
    }
  }, [config])

  const handleTemplateChange = useCallback((value: string) => {
    const newConfig = { ...config }
    newConfig.templateId = parseInt(value, 10)
    setConfig(newConfig)
  }, [config])

  const handleColourChange = useCallback((fieldName: keyof BadgeConfiguration) => {
    return (color: string) => {
      const newConfig = { ...config }
      newConfig[fieldName] = color
      setConfig(newConfig)
    }
  }, [config])

  const handleFileUpload = useCallback((fieldName: 'logoImage' | 'backImage') => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      util.FileToBase64(file, (base64: string) => {
        const newConfig = { ...config }
        if (fieldName === 'logoImage') {
          newConfig.logoImageContentType = file.type
          newConfig.base64LogoImage = base64
        } else {
          newConfig.backImageContentType = file.type
          newConfig.base64BackImage = base64
        }
        setConfig(newConfig)
      })
    }
  }, [config])

  const clearImage = useCallback((fieldName: 'logoImage' | 'backImage') => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const newConfig = { ...config }
      if (fieldName === 'logoImage') {
        newConfig.logoImageContentType = null
        newConfig.base64LogoImage = null
      } else {
        newConfig.backImageContentType = null
        newConfig.base64BackImage = null
      }
      setConfig(newConfig)
    }
  }, [config])

  const handlePreviewBadge = async () => {
    if (!eventId) return

    try {
      // For layout-based templates, preview directly from the template
      if (selectedTemplate?.isLayoutBased) {
        BadgePrintingLogic(() => BadgeTemplateEndpoints.previewTemplate(
          selectedTemplate.id,
          config.propertyOverrides,
          config.base64LogoImage,
          config.logoImageContentType,
        ))
          .catch(err => {
            console.error('Badge preview error:', err)
            toast.error('An error occurred while generating the badge preview')
          })
        return
      }

      // For SVG templates, save first then preview via the event endpoint
      await handleSave()

      BadgePrintingLogic(() => EventEndpoints.getBadgePreview(parseInt(eventId)))
        .catch(err => {
          console.error('Badge preview error:', err)
          toast.error('An error occurred while generating the badge preview')
        })
    } catch (error) {
      console.error('Failed to save before preview:', error)
      toast.error('Please save the configuration before previewing')
    }
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  const renderImageUpload = (
    fieldName: 'logoImage' | 'backImage',
    label: string,
    currentImage: string | null,
    currentType: string | null
  ) => {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 cursor-pointer">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload(fieldName)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={saving || !canEdit}
          />

          {currentImage ? (
            <>
              <img
                src={`data:${currentType};base64,${currentImage}`}
                alt="Preview"
                className="max-w-full max-h-32 mx-auto rounded"
              />
              <Button
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2"
                onClick={clearImage(fieldName)}
                disabled={saving || !canEdit}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <div className="text-center">
              <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                <em>Click here to browse for an image, or drag the file within this box.</em>
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Badge Configuration
          </CardTitle>
          <CardDescription>
            Customize the appearance and layout of event badges
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="badgeName">Badge Name</Label>
              <Input
                id="badgeName"
                type="text"
                value={config.badgeName}
                onChange={handleInputChange('badgeName')}
                disabled={saving || !canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="templateId">Badge Layout</Label>
              <Select
                value={config.templateId.toString()}
                onValueChange={handleTemplateChange}
                disabled={saving || !canEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id.toString()}>
                      {template.name} {template.isLayoutBased ? '(Visual)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate && (
                <p className="text-xs text-muted-foreground">
                  {selectedTemplate.orientation} &middot; {selectedTemplate.width}×{selectedTemplate.height}mm
                  {selectedTemplate.isLayoutBased && ' · Visual builder template'}
                </p>
              )}
            </div>
          </div>

          {/* Colors - only for SVG-based templates */}
          {selectedTemplate && !selectedTemplate.isLayoutBased ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <ColorPicker
                  label="Primary Colour"
                  color={config.primaryColour}
                  onChange={handleColourChange('primaryColour')}
                  disabled={saving || !canEdit}
                />
              </div>

              <div className="space-y-2">
                <ColorPicker
                  label="Secondary Colour"
                  color={config.secondaryColour}
                  onChange={handleColourChange('secondaryColour')}
                  disabled={saving || !canEdit}
                />
              </div>

              <div className="space-y-2">
                <ColorPicker
                  label="Role Text Colour"
                  color={config.roleTextColour}
                  onChange={handleColourChange('roleTextColour')}
                  disabled={saving || !canEdit}
                />
              </div>
            </div>
          ) : selectedTemplate?.isLayoutBased ? (
            <BadgePropertyOverrides
              template={selectedTemplate}
              overrides={config.propertyOverrides}
              onChange={(overrides) => setConfig(prev => ({ ...prev, propertyOverrides: overrides }))}
              disabled={saving || !canEdit}
            />
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div className="space-y-2">
              {renderImageUpload(
                'logoImage',
                'Logo Image',
                config.base64LogoImage,
                config.logoImageContentType
              )}
            </div>

            <div className="space-y-2 md:col-span-2">
              {renderImageUpload(
                'backImage',
                'Image on Back',
                config.base64BackImage,
                config.backImageContentType
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              disabled={saving}
              onClick={handlePreviewBadge}
            >
              Preview Badge
            </Button>
            <Button onClick={handleSave} disabled={saving || !canEdit}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
